import CloseIcon from "@mui/icons-material/Close";
import DirectionsWalkOutlinedIcon from "@mui/icons-material/DirectionsWalkOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import classNames from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setCachedAnimationDetails } from "../../../app/slices/AnimationsSlice";
import { AnimationDetailsDTO } from "../../../dto/AnimationDetailsDTO";
import {
  MessageBubbleAnimation,
  noMessageBubbleAnimation,
  normalizeMessageBubbleAnimation,
} from "../../../models/interactions/MessageBubble";
import AnimationLibrary from "./AnimationLibrary";
import { ANIMATION_UNAVAILABLE_LABEL, getAnimationDisplayLabel } from "./animationDisplay";
import { useAnimationDetails, useAnimationRender } from "./useAnimationAssets.hook";

interface PiAnimationPickerProps {
  selectedAnimation: MessageBubbleAnimation | string | undefined;
  onChange: (animation: MessageBubbleAnimation) => void;
  /** Tile height in px (PiAnimationPicker only). Defaults to 200. */
  height?: number;
  /** The speaker can't animate (a prop): show why instead of a picker. */
  disabled?: boolean;
}

/**
 * Shared state for the animation pickers: the chosen animation, its label and
 * render, and the Animation Library dialog. Picking saves the same fields and
 * caches the details exactly like MessageBubbleAnimationDropdown.
 */
export function useAnimationSelection(
  selectedAnimation: MessageBubbleAnimation | string | undefined,
  onChange: (animation: MessageBubbleAnimation) => void
) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const normalized = useMemo(
    () => normalizeMessageBubbleAnimation(selectedAnimation),
    [selectedAnimation]
  );
  const animationId = normalized.animationId || undefined;
  const { details, isUnavailable } = useAnimationDetails(animationId);
  const { renderUri, isUnavailable: isRenderUnavailable } = useAnimationRender(animationId);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => {
    setIsImageLoaded(false);
    setHasImageError(false);
  }, [renderUri]);

  // Same rule as MessageBubbleAnimationDropdown: "none" without a name is no animation.
  const hasSelection = Boolean(
    (normalized.name || normalized.animationState) &&
      !(!normalized.name && normalized.animationState.trim().toLowerCase() === "none")
  );
  const label = hasSelection
    ? getAnimationDisplayLabel(
        details ?? { name: normalized.name, animationState: normalized.animationState }
      )
    : String(t("animation_picker.none"));

  const showImage = hasSelection && Boolean(renderUri) && !hasImageError;
  const isLoading =
    hasSelection &&
    ((Boolean(animationId) && !renderUri && !isRenderUnavailable && !isUnavailable && !hasImageError) ||
      (showImage && !isImageLoaded));
  const message = !hasSelection
    ? undefined
    : isUnavailable
    ? ANIMATION_UNAVAILABLE_LABEL
    : !showImage && !isLoading
    ? String(t("animation_picker.no_preview"))
    : undefined;

  const select = (picked: AnimationDetailsDTO) => {
    onChange({
      name: picked.name,
      animationState: picked.animationState,
      layer: picked.layer,
      animationId: picked.externalId,
    });
    dispatch(
      setCachedAnimationDetails({ externalId: picked.externalId, animationDetails: picked })
    );
  };

  const clear = () => onChange({ ...noMessageBubbleAnimation });
  const library = isLibraryOpen ? (
    <AnimationLibrary
      isOpen={isLibraryOpen}
      onClose={() => setIsLibraryOpen(false)}
      title="Animation Library"
      width="calc(90vw / var(--app-zoom, 1))"
      height="calc(90vh / var(--app-zoom, 1))"
      onSubmit={select}
    />
  ) : null;

  return {
    label,
    hasSelection,
    isUnavailable,
    isLoading,
    message,
    renderUri: showImage ? renderUri : undefined,
    isLibraryOpen,
    openLibrary: () => setIsLibraryOpen(true),
    clear,
    library,
    onImageLoad: () => setIsImageLoaded(true),
    onImageError: () => {
      setHasImageError(true);
      setIsImageLoaded(false);
    },
  };
}

/**
 * Animation picker for the reworked previews (Design Studio
 * `screens/lookat-rework/r2d-anim-b.html`): the preview tile is the control.
 * Clicking it opens the Animation Library; × clears. It keeps every state the
 * old picker had: nothing chosen, loading, deleted animation, and animations
 * without a render.
 */
export default function PiAnimationPicker({
  selectedAnimation,
  onChange,
  height = 200,
  disabled,
}: PiAnimationPickerProps) {
  const { t } = useTranslation();
  const {
    label,
    hasSelection,
    isUnavailable,
    isLoading,
    message,
    renderUri,
    openLibrary,
    clear,
    library,
    onImageLoad,
    onImageError,
  } = useAnimationSelection(selectedAnimation, onChange);
  const showImage = Boolean(renderUri);

  if (disabled) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-2 w-full px-6 box-border rounded-[6px] border border-dashed border-interaction-primary-faded bg-interaction-primary-washed text-center text-[13px] text-creator-text-faded"
        style={{ height }}
        data-testid="animation-preview"
        aria-disabled="true"
      >
        <DirectionsWalkOutlinedIcon className="text-interaction-primary-faded" sx={{ fontSize: 32 }} />
        {String(t("animation_picker.unavailable_for_props"))}
      </div>
    );
  }

  return (
    <div
      className={classNames(
        "relative w-full overflow-hidden rounded-[6px] border bg-interaction-primary-washed transition-colors",
        "hover:border-interaction-primary focus-within:border-interaction-primary",
        {
          "border-solid border-interaction-primary-faded": hasSelection,
          "border-dashed border-interaction-primary-faded": !hasSelection,
        }
      )}
      style={{ height }}
      data-testid="animation-preview"
    >
      {/* One button over the whole tile; the clear button sits on top of it. */}
      <button
        type="button"
        onClick={openLibrary}
        aria-label={String(t(hasSelection ? "animation_picker.change" : "animation_picker.choose"))}
        className="absolute inset-0 flex flex-col w-full p-0 border-none bg-transparent text-left cursor-pointer outline-none"
        data-testid="select-animation"
      >
        <span className="relative flex-1 w-full min-h-0">
          {isLoading && <span className="absolute inset-0 animate-pulse bg-creator-surface" />}
          {showImage && (
            <img
              className="absolute inset-0 w-full h-full object-contain p-2 box-border"
              src={renderUri}
              alt={label}
              onLoad={onImageLoad}
              onError={onImageError}
            />
          )}
          {!hasSelection && (
            <span className="absolute inset-0 flex items-center justify-center text-interaction-primary-faded">
              <DirectionsWalkOutlinedIcon sx={{ fontSize: 36 }} />
            </span>
          )}
          {message && (
            <span
              className="absolute inset-0 flex items-center justify-center px-6 text-center text-[13px] text-creator-text-sub"
              data-testid={isUnavailable ? "animation-unavailable" : undefined}
            >
              {message}
            </span>
          )}
        </span>
        <span className="flex flex-none items-center gap-2 w-full h-[44px] px-3 box-border bg-creator-surface border-t border-solid border-x-0 border-b-0 border-interaction-primary-washed">
          <span
            className={classNames("min-w-0 flex-1 truncate text-[14px]", {
              "font-semibold text-interaction-primary": hasSelection,
              "font-medium text-creator-text-faded": !hasSelection,
            })}
          >
            {label}
          </span>
          <span className="inline-flex flex-none items-center gap-1.5 text-[13px] font-medium text-interaction-primary">
            <SwapHorizIcon sx={{ fontSize: 16 }} />
            {String(t(hasSelection ? "animation_picker.change" : "animation_picker.choose"))}
          </span>
        </span>
      </button>

      {hasSelection && (
        <button
          type="button"
          onClick={clear}
          aria-label={String(t("animation_picker.clear"))}
          title={String(t("animation_picker.clear"))}
          className="absolute top-2 right-2 flex items-center justify-center w-[28px] h-[28px] rounded-full border-none bg-creator-surface text-interaction-primary cursor-pointer shadow-[0_1px_3px_rgba(16,24,40,0.12)] transition-colors hover:text-creator-error"
          data-testid="clear-animation"
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </button>
      )}

      {library}
    </div>
  );
}

/**
 * Compact animation button for tight spots like the conversation composer:
 * the chosen animation's thumbnail and name, opening the Animation Library.
 * Deliberately not chip-shaped (no chevron): it opens a picker, not a menu.
 */
export function PiAnimationButton({
  selectedAnimation,
  onChange,
  disabled,
  fit,
}: PiAnimationPickerProps & { fit?: boolean }) {
  const { t } = useTranslation();
  const { label, hasSelection, renderUri, isLibraryOpen, openLibrary, library } =
    useAnimationSelection(selectedAnimation, onChange);

  return (
    <>
      <button
        type="button"
        onClick={openLibrary}
        disabled={disabled}
        title={String(
          t(
            disabled
              ? "animation_picker.unavailable_for_props"
              : hasSelection
              ? "animation_picker.change"
              : "animation_picker.choose"
          )
        )}
        aria-label={String(t(hasSelection ? "animation_picker.change" : "animation_picker.choose"))}
        className={classNames(
          "inline-flex flex-none items-center gap-2 h-[40px] px-2.5 box-border overflow-hidden",
          fit ? "w-auto max-w-[220px]" : "w-[184px]",
          "rounded-[6px] border border-solid bg-interaction-primary-washed text-[13px] font-medium whitespace-nowrap cursor-pointer outline-none transition-colors",
          "focus-visible:border-interaction-primary disabled:opacity-60 disabled:cursor-not-allowed",
          {
            "hover:border-interaction-primary": !disabled,
            "border-interaction-primary": isLibraryOpen,
            "border-interaction-primary-faded": !isLibraryOpen,
            "text-interaction-primary": hasSelection,
            "text-creator-text-faded": !hasSelection,
          }
        )}
        data-testid="select-animation"
      >
        {renderUri ? (
          <img
            className="flex-none w-[24px] h-[24px] rounded-[4px] bg-creator-surface object-contain"
            src={renderUri}
            alt=""
          />
        ) : (
          <DirectionsWalkOutlinedIcon className="flex-none" sx={{ fontSize: 18 }} />
        )}
        <span className={classNames("min-w-0 truncate text-left", { "flex-1": !fit })}>{label}</span>
        <SwapHorizIcon className="flex-none opacity-80" sx={{ fontSize: 16 }} />
      </button>
      {library}
    </>
  );
}

/** Display name of an animation ("No animation" when none), for read-only summaries. */
export function useAnimationLabel(animation: MessageBubbleAnimation | string | undefined): string {
  const { t } = useTranslation();
  const normalized = useMemo(() => normalizeMessageBubbleAnimation(animation), [animation]);
  const { details } = useAnimationDetails(normalized.animationId || undefined);
  const hasSelection = Boolean(
    (normalized.name || normalized.animationState) &&
      !(!normalized.name && normalized.animationState.trim().toLowerCase() === "none")
  );
  return hasSelection
    ? getAnimationDisplayLabel(
        details ?? { name: normalized.name, animationState: normalized.animationState }
      )
    : String(t("animation_picker.none"));
}
